import React, { useState, useEffect, useCallback, memo, useRef } from 'react';
import {
  Typography,
  Box,
  Paper,
  Grid,
  TextField,
  Button,
  IconButton,
  useTheme,
  Alert,
  alpha,
  CircularProgress,
  Chip,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions
} from '@mui/material';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import MapIcon from '@mui/icons-material/Map';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import GooglePlacesAutocomplete from './GooglePlacesAutocomplete';
import { loadGoogleMapsAPI, isGoogleMapsLoaded } from './utils/googleMapsLoader';
import LocationMap from './LocationMap';
import axios from '../../utils/axiosConfig';

// Standalone LocationInfo Component - Dedicated Location List Management
const LocationInfo = memo(({ projectId, onLocationsChanged }) => {
  const [googleMapsLoaded, setGoogleMapsLoaded] = useState(false);
  const [locations, setLocations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saveStatus, setSaveStatus] = useState('');
  const theme = useTheme();
  
  // Consistent text field styling
  const textFieldSx = {
    '& .MuiInputLabel-root': {
      fontSize: '0.95rem',
      fontWeight: 600
    },
    '& .MuiOutlinedInput-root': {
      backgroundColor: theme.palette.mode === 'dark'
        ? alpha(theme.palette.background.default, 0.3)
        : alpha(theme.palette.grey[50], 0.5),
      borderRadius: 2.5,
      transition: 'all 0.2s ease',
      '& fieldset': {
        borderWidth: 1.5
      },
      '&:hover': {
        backgroundColor: theme.palette.mode === 'dark'
          ? alpha(theme.palette.background.default, 0.4)
          : alpha(theme.palette.grey[50], 0.8),
        '& fieldset': {
          borderColor: theme.palette.primary.main
        }
      },
      '&.Mui-focused': {
        backgroundColor: theme.palette.mode === 'dark'
          ? alpha(theme.palette.primary.main, 0.05)
          : alpha(theme.palette.primary.main, 0.02),
        '& fieldset': {
          borderWidth: 2
        }
      }
    }
  };
  
  // Persistent name tracking to prevent reset when address is selected
  const persistentLocationNames = useRef({});
  
  // Track which locations are currently being saved to prevent duplicates
  const savingLocations = useRef(new Set());
  const [mapOpen, setMapOpen] = useState(false);
  const [mapLocation, setMapLocation] = useState(null);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [pendingDeleteIndex, setPendingDeleteIndex] = useState(null);
  const draftsKey = React.useMemo(() => `project:${projectId}:locationDrafts`, [projectId]);
  
  // Load locations from API and restore drafts on component mount
  useEffect(() => {
    const fetchLocations = async () => {
      if (!projectId) return;
      
      try {
        setLoading(true);
        const { data: locationsData } = await axios.get(`/api/v1/locations/${projectId}`);
        // Merge saved drafts (isNew) from localStorage
        let merged = locationsData;
        try {
          const rawDrafts = localStorage.getItem(draftsKey);
          const drafts = rawDrafts ? JSON.parse(rawDrafts) : [];
          if (Array.isArray(drafts) && drafts.length) {
            merged = [...locationsData, ...drafts.map(d => ({ ...d, isNew: true }))];
          }
        } catch {}
        setLocations(merged);
        console.log('✅ Loaded locations from API:', locationsData);
      } catch (error) {
        console.error('❌ Error fetching locations:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchLocations();
  }, [projectId, draftsKey]);

  // Load Google Maps API
  useEffect(() => {
    const initializeGoogleMaps = async () => {
      try {
        if (!isGoogleMapsLoaded()) {
          await loadGoogleMapsAPI();
        }
        setGoogleMapsLoaded(true);
      } catch (error) {
        console.error('Failed to load Google Maps API:', error);
        setGoogleMapsLoaded(false);
      }
    };

    initializeGoogleMaps();
  }, []);

  // Save location to dedicated locations API (with duplicate prevention)
  const saveLocation = useCallback(async (locationData, locationIndex) => {
    if (!projectId || !locationData.name || !locationData.address) {
      console.error('❌ SAVE FAILED: Missing required data');
      return null;
    }
    
    // Prevent duplicate saves for the same location
    const saveKey = `${locationIndex}_${locationData.name}_${locationData.address}`;
    if (savingLocations.current.has(saveKey)) {
      console.log('⏳ SAVE SKIPPED: Already saving this location');
      return null;
    }
    
    savingLocations.current.add(saveKey);
    
    try {
      setSaveStatus('Saving...');
      console.log('💾 Saving location to API:', locationData);
      
      const { data: savedLocation } = await axios.post('/api/v1/locations', {
        projectId,
        name: locationData.name.trim(),
        address: locationData.address.trim()
      });
      
      console.log('✅ Location saved successfully:', savedLocation);
      setSaveStatus('Saved ✓');
      setTimeout(() => setSaveStatus(''), 2000);
      if (typeof onLocationsChanged === 'function') {
        try {
          onLocationsChanged();
        } catch (e) {
          console.warn('onLocationsChanged callback threw an error:', e);
        }
      }
      return savedLocation;
    } catch (error) {
      console.error('❌ Error saving location:', error);
      setSaveStatus('Save failed ✗');
      setTimeout(() => setSaveStatus(''), 3000);
      return null;
    } finally {
      // Remove from saving set after attempt
      savingLocations.current.delete(saveKey);
    }
  }, [projectId]);

  // Add new location (local state only, saved when complete)
  const handleAddLocation = useCallback(() => {
    const newLocation = {
      _id: `temp_${Date.now()}`,
      name: '',
      address: '',
      isNew: true
    };
    
    setLocations(prev => [...prev, newLocation]);
    console.log('➕ Added new location to local state');
  }, []);

  // Update location field and auto-save when complete
  const handleUpdateLocation = useCallback(async (index, field, value) => {
    const updatedLocations = [...locations];
    
    // Store names persistently to prevent reset during address selection
    if (field === 'name') {
      persistentLocationNames.current[index] = value;
      console.log('💾 Storing persistent name for index', index, ':', value);
    }
    
    updatedLocations[index] = { ...updatedLocations[index], [field]: value };
    
    setLocations(updatedLocations);
    // Persist drafts: save only isNew items to localStorage
    try {
      const drafts = updatedLocations.filter(l => l?.isNew);
      localStorage.setItem(draftsKey, JSON.stringify(drafts));
    } catch {}
    
    // Only log field updates - no auto-save here to prevent duplicates
    console.log('📝 Location field updated:', {
      field,
      value,
      locationIndex: index
    });
  }, [locations, saveLocation]);
  
  // Handle address selection from GooglePlacesAutocomplete
  const handlePlaceSelect = useCallback(async (index, place) => {
    const address = place.formatted_address || place.name || '';
    const updatedLocations = [...locations];
    const location = updatedLocations[index];
    
    // Preserve the existing name using persistent ref
    const preservedName = persistentLocationNames.current[index] || location?.name || '';
    
    console.log('🏠 Place selected for index', index, ':', {
      address,
      preservedName,
      persistentName: persistentLocationNames.current[index],
      currentName: location?.name
    });
    
    // Update with both preserved name and new address
    updatedLocations[index] = {
      ...location,
      name: preservedName,
      address: address
    };
    
    setLocations(updatedLocations);
    
    // Auto-save only when address is selected (single save point)
    if (preservedName?.trim() && address?.trim() && location.isNew) {
      console.log('✅ Location complete - saving to API (single save)');
      const savedLocation = await saveLocation({
        name: preservedName,
        address: address
      }, index);
      
      if (savedLocation) {
        // Replace temp location with saved location
        const finalLocations = [...updatedLocations];
        finalLocations[index] = { ...savedLocation, isNew: false };
        setLocations(finalLocations);
      }
    }
  }, [locations, saveLocation]);

  // Delete location
  const handleDeleteLocation = useCallback(async (index) => {
    const location = locations[index];
    
    if (location.isNew) {
      // Just remove from local state if not saved yet
      setLocations(prev => prev.filter((_, i) => i !== index));
      // Update drafts in localStorage
      try {
        const drafts = locations.filter((_, i) => i !== index).filter(l => l?.isNew);
        localStorage.setItem(draftsKey, JSON.stringify(drafts));
      } catch {}
    } else {
      // Delete from API if already saved
      try {
        const response = await fetch(`/api/v1/locations/${location._id}`, {
          method: 'DELETE',
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('token')}`
          }
        });
        
        if (response.ok) {
          setLocations(prev => prev.filter((_, i) => i !== index));
          try {
            const drafts = locations.filter((_, i) => i !== index).filter(l => l?.isNew);
            localStorage.setItem(draftsKey, JSON.stringify(drafts));
          } catch {}
          console.log('✅ Location deleted successfully');
          if (typeof onLocationsChanged === 'function') {
            try {
              onLocationsChanged();
            } catch (e) {
              console.warn('onLocationsChanged callback threw an error:', e);
            }
          }
        } else {
          console.error('❌ Failed to delete location');
        }
      } catch (error) {
        console.error('❌ Error deleting location:', error);
      }
    }
  }, [locations, draftsKey, onLocationsChanged]);

  return (
    <Paper 
      elevation={0}
      sx={{ 
        p: { xs: 3, md: 4 }, 
        mb: 4,
        borderRadius: 2,
        backgroundColor: theme.palette.mode === 'dark' ? '#16181a' : '#fff',
        boxShadow: theme.palette.mode === 'dark' 
          ? '0 8px 32px rgba(0,0,0,0.4)' 
          : '0 8px 32px rgba(0, 0, 0, 0.08)',
        backdropFilter: 'blur(8px)'
      }}>
      
      
      {/* Header */}
      <Box sx={{ 
        display: 'flex', 
        justifyContent: 'space-between', 
        alignItems: 'center',
        mb: 2,
        pb: 2,
        borderBottom: `2px solid ${alpha(theme.palette.primary.main, 0.12)}`,
        position: 'relative',
        '&::after': {
          content: '""',
          position: 'absolute',
          bottom: -2,
          left: 0,
          width: '60px',
          height: '2px',
          background: theme.palette.primary.main,
          borderRadius: 1
        }
      }}>
        <Typography 
          variant="h4" 
          component="h2"
          sx={{ 
            display: 'flex', 
            alignItems: 'center',
            fontWeight: 600,
            letterSpacing: '-0.25px',
            fontSize: '2rem',
            color: theme.palette.text.primary
          }}
        >
          <LocationOnIcon 
            sx={{ 
              mr: 2, 
              fontSize: '2rem',
              color: theme.palette.primary.main,
              filter: 'drop-shadow(0 2px 4px rgba(33, 150, 243, 0.2))'
            }} 
          />
          LOCATIONS
        </Typography>
        
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          {saveStatus && (
            <Typography 
              variant="body2" 
              sx={{ 
                color: saveStatus.includes('✓') ? 'success.main' : 
                       saveStatus.includes('✗') ? 'error.main' : 'text.secondary',
                fontWeight: 500
              }}
            >
              {saveStatus}
            </Typography>
          )}
          <Chip size="small" label={`${locations.length} ${locations.length === 1 ? 'location' : 'locations'}`} />
          
          <Tooltip title="Add location" arrow>
            <span>
              <IconButton
                onClick={handleAddLocation}
                aria-label="Add location"
                sx={{
                  width: 38,
                  height: 38,
                  borderRadius: 1,
                  border: `1px solid ${alpha(theme.palette.primary.main, 0.25)}`,
                  color: theme.palette.primary.main,
                  backgroundColor: 'transparent',
                  '&:hover': {
                    backgroundColor: alpha(theme.palette.primary.main, 0.12)
                  }
                }}
              >
                <AddIcon fontSize="small" />
              </IconButton>
            </span>
          </Tooltip>
        </Box>
      </Box>

      {/* Locations List */}
      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
          <CircularProgress />
        </Box>
      ) : locations.length === 0 ? (
        <Box 
          sx={{ 
            p: 3, 
            textAlign: 'center',
            border: '2px dashed',
            borderColor: 'divider',
            borderRadius: 2,
            backgroundColor: theme.palette.mode === 'dark' ? 'rgba(30, 30, 35, 0.2)' : 'rgba(0, 0, 0, 0.02)'
          }}
        >
          <Typography color="textSecondary" variant="body1">
            No locations added yet. Click "Add Location" to get started.
          </Typography>
        </Box>
      ) : (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          {locations.map((location, index) => (
            <Paper
              key={location.id || index}
              elevation={0}
              sx={{
                p: 3,
                borderRadius: 1,
                border: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
                backgroundColor: theme.palette.mode === 'dark' 
                  ? alpha(theme.palette.background.paper, 0.6)
                  : theme.palette.background.paper,
                boxShadow: theme.palette.mode === 'dark'
                  ? '0 4px 16px rgba(0,0,0,0.3)'
                  : '0 2px 8px rgba(0,0,0,0.06)'
              }}
            >
              <Grid container spacing={2} alignItems="center">
                <Grid item xs={12} sm={5}>
                  <TextField
                    fullWidth
                    label="Location Name"
                    value={location.name || ''}
                    onChange={(e) => handleUpdateLocation(index, 'name', e.target.value)}
                    placeholder="Enter location name..."
                    size="small"
                    sx={textFieldSx}
                  />
                </Grid>
                
                <Grid item xs={12} sm={6}>
                  {googleMapsLoaded ? (
                    <GooglePlacesAutocomplete
                      value={location.address || ''}
                      onChange={(value) => handleUpdateLocation(index, 'address', value)}
                      onPlaceSelect={(place) => handlePlaceSelect(index, place)}
                      placeholder="Enter or search for address..."
                      size="small"
                      sx={textFieldSx}
                    />
                  ) : (
                    <TextField
                      fullWidth
                      label="Address"
                      value={location.address || ''}
                      onChange={(e) => handleUpdateLocation(index, 'address', e.target.value)}
                      onBlur={async () => {
                        // Auto-save when user finishes typing address manually
                        const currentLocation = locations[index];
                        const preservedName = persistentLocationNames.current[index] || currentLocation?.name || '';
                        if (preservedName?.trim() && currentLocation.address?.trim() && currentLocation.isNew) {
                          console.log('✅ Location complete via manual address input - saving to API');
                          const savedLocation = await saveLocation({
                            name: preservedName,
                            address: currentLocation.address
                          }, index);
                          
                          if (savedLocation) {
                            const finalLocations = [...locations];
                            finalLocations[index] = { ...savedLocation, isNew: false };
                            setLocations(finalLocations);
                          }
                        }
                      }}
                      placeholder="Enter address..."
                      size="small"
                      sx={textFieldSx}
                    />
                  )}
                </Grid>
                
                <Grid item xs={12} sm={1}>
                  <Box sx={{ display: 'flex', gap: 0.5, justifyContent: { xs: 'flex-start', sm: 'flex-end' } }}>
                    <Tooltip title="Open map">
                      <IconButton aria-label={`open map for ${location.name || 'location'}`} size="small" onClick={() => { setMapLocation(location); setMapOpen(true); }}>
                        <MapIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Copy address">
                      <IconButton aria-label="copy address" size="small" onClick={async () => { try { await navigator.clipboard.writeText(location.address || ''); } catch {} }}>
                        <ContentCopyIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Delete">
                      <IconButton 
                        color="error" 
                        onClick={() => { setPendingDeleteIndex(index); setConfirmOpen(true); }}
                        size="small"
                        sx={{ '&:hover': { backgroundColor: alpha(theme.palette.error.main, 0.1) } }}
                      >
                        <DeleteIcon />
                      </IconButton>
                    </Tooltip>
                  </Box>
                </Grid>
              </Grid>
              
              {/* Show completion status */}
              {location.name && location.address && (
                <Box sx={{ mt: 2 }}>
                  <Alert 
                    severity="success" 
                    variant="outlined"
                    sx={{ 
                      py: 0.5,
                      '& .MuiAlert-message': {
                        fontSize: '0.875rem'
                      }
                    }}
                  >
                    Location complete: {location.name} - {location.address}
                    {/* Compact View on map link */}
                    <Box sx={{ mt: 0.5 }}>
                      <Button size="small" onClick={() => { setMapLocation(location); setMapOpen(true); }} sx={{ textTransform: 'none', px: 0 }}>
                        View on map
                      </Button>
                    </Box>
                  </Alert>
                </Box>
              )}
            </Paper>
          ))}
        </Box>
      )}
      {/* Map Dialog */}
      <LocationMap open={mapOpen} onClose={() => setMapOpen(false)} location={mapLocation} />
      {/* Delete Confirmation */}
      <Dialog open={confirmOpen} onClose={() => setConfirmOpen(false)}>
        <DialogTitle>Delete location?</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary">This action cannot be undone.</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmOpen(false)}>Cancel</Button>
          <Button color="error" variant="contained" onClick={() => { const idx = pendingDeleteIndex; setConfirmOpen(false); setPendingDeleteIndex(null); if (idx !== null) handleDeleteLocation(idx); }}>Delete</Button>
        </DialogActions>
      </Dialog>
    </Paper>
  );
})
;

export default LocationInfo;
